import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef } from 'react';
import cx from 'clsx';
import { useTheme } from '../../ThemeProvider';
import classes from './Slider.styles.less';
function getClientPosition(e) {
    const { touches } = e;
    if (touches && touches.length) {
        const finger = touches[0];
        return {
            x: finger.clientX,
            y: finger.clientY,
        };
    }
    return {
        x: e.clientX,
        y: e.clientY,
    };
}
export default function Slider({ value = 50, min = 0, max = 100, step = 1, trackSize = 200, onChange, }) {
    const [theme] = useTheme();
    const container = useRef(null);
    const handle = useRef(null);
    const start = useRef({ x: 0, y: 0 });
    const offset = useRef({ x: 0, y: 0 });
    function getPosition() {
        let left = ((value - min) / (max - min)) * 100;
        if (left > 100) {
            left = 100;
        }
        if (left < 0) {
            left = 0;
        }
        return left;
    }
    function change(val) {
        if (!container.current) {
            return;
        }
        let left = val;
        const { width } = container.current.getBoundingClientRect();
        let dx = 0;
        if (left < 0) {
            left = 0;
        }
        if (left > width) {
            left = width;
        }
        dx = (left / width) * (max - min);
        onChange((dx !== 0 ? Math.round(dx / step) * step : 0) + min);
    }
    function getPos(e) {
        return getClientPosition(e).x + start.current.x - offset.current.x;
    }
    function handleDrag(e) {
        e.preventDefault();
        change(getPos(e));
    }
    function handleDragEnd(e) {
        e.preventDefault();
        document.removeEventListener('mousemove', handleDrag);
        document.removeEventListener('mouseup', handleDragEnd);
        document.removeEventListener('touchmove', handleDrag);
        document.removeEventListener('touchend', handleDragEnd);
        document.removeEventListener('touchcancel', handleDragEnd);
    }
    function handleMouseDown(e) {
        e.preventDefault();
        e.stopPropagation();
        e.nativeEvent.stopImmediatePropagation();
        const dom = handle.current;
        if (!dom) {
            return;
        }
        const clientPos = getClientPosition(e);
        start.current = {
            x: dom.offsetLeft,
            y: dom.offsetTop,
        };
        offset.current = {
            x: clientPos.x,
            y: clientPos.y,
        };
        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('touchmove', handleDrag, { passive: false });
        document.addEventListener('touchend', handleDragEnd);
        document.addEventListener('touchcancel', handleDragEnd);
    }
    function handleTrackMouseDown(e) {
        e.preventDefault();
        if (!container.current) {
            return;
        }
        const clientPos = getClientPosition(e);
        const rect = container.current.getBoundingClientRect();
        start.current = {
            x: clientPos.x - rect.left,
            y: clientPos.y - rect.top,
        };
        offset.current = {
            x: clientPos.x,
            y: clientPos.y,
        };
        document.addEventListener('mousemove', handleDrag);
        document.addEventListener('mouseup', handleDragEnd);
        document.addEventListener('touchmove', handleDrag, { passive: false });
        document.addEventListener('touchend', handleDragEnd);
        document.addEventListener('touchcancel', handleDragEnd);
        change(clientPos.x - rect.left);
    }
    const position = getPosition();
    return (_jsxs("div", { ref: container, className: cx(classes.track, classes[theme]), onTouchStart: handleTrackMouseDown, onMouseDown: handleTrackMouseDown, onKeyDown: (event) => {
            if (event.key === 'ArrowLeft') {
                change(value - step);
            }
            if (event.key === 'ArrowRight') {
                change(value + step);
            }
        }, role: "slider", tabIndex: 0, "aria-valuemin": min, "aria-valuemax": max, "aria-valuenow": value, style: { width: trackSize }, children: [_jsx("div", { className: classes.active, style: { width: `${position}%` } }), _jsx("div", { ref: handle, style: {
                    position: 'absolute',
                    transform: 'translate(-50%, -50%)',
                    left: `${position}%`,
                    top: '50%',
                }, onTouchStart: handleMouseDown, onMouseDown: handleMouseDown, onClick: (e) => {
                    e.stopPropagation();
                    e.nativeEvent.stopImmediatePropagation();
                }, onKeyDown: (e) => {
                    e.stopPropagation();
                }, role: "button", tabIndex: 0, children: _jsx("div", { className: classes.thumb }) })] }));
}
